import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { addUserDestination, getUser } from "../../state/User";
import Center from "../utils/Center";
import Loading from "../utils/Loading";
import ErrorCard from "../utils/ErrorCard";
import {
  addTwitterDestination,
  getTwitterOAuthProcessParams,
  handleTwitterOAuth,
} from "./Twitter";
import Destination from "./Destination";
import { AddFacebookDestination } from "./Facebook";
import { AddMailchimpDestination } from "./Mailchimp";
import { AddYouTubeDestination } from "./Youtube";
import { connectToCustomBackend } from "./CustomBackend";
import { AddLinkedInConnection } from "./LinkedIn";
import { groupDestinations } from "./Group";
import { verifyRealUser } from "../helpers";

export default function Destinations() {
  const user = getUser();
  const [error, setError] = useState<[string, string] | null>(null);
  const [inProgress, setProgressFlag] = useState(false);
  const twitterOAuthParams = getTwitterOAuthProcessParams();
  const history = useHistory();

  useEffect(() => {
    if (user && twitterOAuthParams) {
      (async () => {
        setProgressFlag(true);
        try {
          const res = await handleTwitterOAuth(user, twitterOAuthParams);
          addUserDestination(res.destination);
        } catch (err) {
          console.error(err);
          setError(["Error linking a Twitter account.", "/help/twitter"]);
        }
        setProgressFlag(false);
        history.push("/destinations");
      })();
    }
  }, []);

  if (inProgress) {
    return (
      <Center width={40}>
        <Loading className="mt3" />
      </Center>
    );
  }

  return (
    <>
      {user && user.destinations && user.destinations.length > 0 ? (
        <>
          <h1 className="tac">Linked destinations</h1>
          <ul className="mt3 mb3">
            {user.destinations
              .sort((a, b) => (a.type < b.type ? 1 : -1))
              .map((d, i) => (
                <Destination key={d.id} destination={d} />
              ))}
          </ul>
        </>
      ) : null}
      <h2 className="tac">Networks & Services</h2>
      <p className="tac mb3 mt1">
        The following networks and services are supported by Contempas. We
        strongly recommend reading the documentation before linking some of
        them.
      </p>
      {error && error[0] && error[1] ? (
        <ErrorCard
          message={
            <>
              {error[0]}
              <br />
              Check out{" "}
              <a href={error[1]} target="_blank">
                this page
              </a>{" "}
              for tips on how to properly link your account/profile.
            </>
          }
          width={500}
        />
      ) : null}

      <div className="grid grid2 gap2 top-bordered">
        {/* ***************************************************** Facebook */}
        <div>
          <h2>
            <img
              src="/assets/img/facebook.png"
              alt="Link a Facebook account"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            Facebook
          </h2>
          <p className="mt1 mb1">
            Schedule posts to your Facebook pages (personal accounts and groups
            are not supported due to Facebook's API restrictions). You will be
            asked to authorize Contempas app so it can post to your pages.
          </p>
          <AddFacebookDestination onError={setError} user={user as User} />
          <a href="/help/facebook" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** Instagram */}
        <div>
          <h2>
            <img
              src="/assets/img/instagram.png"
              alt="Link a Instagram account"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            Instagram
          </h2>
          <p className="mt1 mb1">
            Schedule posts to your Business Instagram accounts (personal
            accounts are not supported due to Instagram's API restrictions)
          </p>
          <AddFacebookDestination
            onError={setError}
            user={user as User}
            instagram
          />
          <a href="/help/instagram" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** Twitter */}
        <div>
          <h2>
            <img
              src="/assets/img/twitter.png"
              alt="Link a Twitter account"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            Twitter
          </h2>
          <p className="mt1 mb1">
            Plan your Twitter presence by scheduling your tweets.
          </p>
          <button
            onClick={() => {
              verifyRealUser(user as User, () => {
                addTwitterDestination(setError);
              });
            }}
            className="secondary fz08"
          >
            <span className="vam">Link a Twitter account</span>
          </button>
          <a href="/help/twitter" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** Mailchimp */}
        <div>
          <h2>
            <img
              src="/assets/img/mailchimp.png"
              alt="Link a Mailchimp account"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            Mailchimp
          </h2>
          <p className="mt1 mb1">
            Schedule your email campaigns with Contempas.
          </p>
          <AddMailchimpDestination onError={setError} user={user as User} />
          <a href="/help/mailchimp" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** YouTube */}
        <div>
          <h2>
            <img
              src="/assets/img/youtube.png"
              alt="Link a YouTube account"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            YouTube
          </h2>
          <p className="mt1 mb1">
            Use Contempas to post videos to your YouTube channels.
          </p>
          <AddYouTubeDestination onError={setError} user={user as User} />
          <a href="/help/youtube" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** LinkedIn */}
        <div>
          <h2>
            <img
              src="/assets/img/linkedin.png"
              alt="Link a LinkedIn account"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            LinkedIn
          </h2>
          <p className="mt1 mb1">Schedule posts to your LinkedIn profile.</p>
          <AddLinkedInConnection onError={setError} user={user as User} />
          <a href="/help/linkedin" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** Custom Backend */}
        <div>
          <h2>
            <img
              src="/assets/img/server.png"
              alt="Link a custom backend"
              width="30"
              height="30"
              className="ib vam mr05"
            />
            Custom back-end
          </h2>
          <p className="mt1 mb1">
            Contempas will send request to your own back-end endpoint.
          </p>
          <button
            onClick={() =>
              verifyRealUser(user as User, () => {
                connectToCustomBackend();
              })
            }
            className="secondary fz08"
          >
            <span className="vam">Link to a custom back-end</span>
          </button>
          <a href="/help/custom-backend" target="_blank" className="ib ml1">
            <small>❯ Documentation</small>
          </a>
        </div>

        {/* ***************************************************** Grouping */}
        {user?.destinations && user.destinations.length > 1 && (
          <div>
            <h2>
              <img
                src="/assets/img/group.png"
                alt="Group multiple destinations"
                width="30"
                height="30"
                className="ib vam mr05"
              />
              Destination group
            </h2>
            <p className="mt1 mb1">
              <small>
                Group multiple destinations into a single entry so you can
                publish quicker.
              </small>
            </p>
            <button
              onClick={() =>
                verifyRealUser(user as User, () => {
                  groupDestinations();
                })
              }
              className="secondary fz08"
            >
              <span className="vam">Create a destination group</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
